import React, { useRef, createContext } from 'react';
import PropTypes from 'prop-types';
import { Modal as BSModal } from 'react-bootstrap';

// Create context for modal
const ModalContext = createContext();

/**
 * Main Modal Component
 * 
 * @param {Object} props
 * @param {boolean} props.isOpen - Controls modal visibility
 * @param {Function} props.onClose - Callback when modal should close
 * @param {React.ReactNode} props.children - Modal content
 * @param {string} props.size - Modal size: 'sm' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | '6xl' | '7xl' (default: 'lg')
 * @param {boolean} props.closeOnOverlayClick - Close modal when clicking overlay (default: true)
 * @param {boolean} props.closeOnEsc - Close modal when pressing ESC (default: true)
 * @param {boolean} props.centered - Vertically center the modal (default: true)
 * @param {boolean} [props.scrollable] - 
 * @param {string} props.className - Additional classes for modal dialog
 * @param {string} props.overlayClassName - Additional classes for backdrop
 * @param {boolean} [props.enableTailwind=false] - Wrap modal content with editor-tailwind-scope for Tailwind support (default: false)
 */
export const Modal = (props) => {
  const {
    isOpen = false,
    onClose,
    children,
    size = 'lg',
    closeOnOverlayClick = true,
    closeOnEsc = true,
    centered = true,
    scrollable,
    className = '',
    overlayClassName = '',
    enableTailwind = false,
    animation = true,
  } = props;
  const modalRef = useRef(null);

  if (!isOpen) return null;

  // For large custom sizes, use 'xl' and let CSS handle the actual width
  const bootstrapSize = size === 'sm' ? 'sm' : size === 'lg' ? 'lg' : 'xl';

  // Map custom sizes to max-width values
  const sizeStyles = {
    '2xl': { maxWidth: '672px' },
    '3xl': { maxWidth: '768px' },
    '4xl': { maxWidth: '896px' },
    '5xl': { maxWidth: '1024px' },
    '6xl': { maxWidth: '1152px' },
    '7xl': { maxWidth: '1280px' },
  };

  // Build the dialog className with custom size styling
  const customSizeClass = sizeStyles[size] ? 'modal-custom-size' : '';
  const dialogClassName = `${className} ${customSizeClass}`.trim();

  return (
    <ModalContext.Provider value={{ onClose }}>
      <BSModal
        show={isOpen}
        onHide={onClose}
        size={bootstrapSize}
        backdrop={closeOnOverlayClick ? true : 'static'}
        keyboard={closeOnEsc}
        dialogClassName={dialogClassName}
        backdropClassName={overlayClassName}
        centered={centered}
        scrollable={scrollable ?? !centered}
        animation={animation}
        ref={modalRef}
      >
        <style>
          {sizeStyles[size] && `
            .modal-custom-size {
              max-width: ${sizeStyles[size].maxWidth} !important;
              width: 95% !important;
            }
          `}
        </style>
        {enableTailwind ? (
          <div className="editor-tailwind-scope">
            {children}
          </div>
        ) : children}
      </BSModal>
    </ModalContext.Provider>
  );
};

Modal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  children: PropTypes.node,
  size: PropTypes.string,
  closeOnOverlayClick: PropTypes.bool,
  closeOnEsc: PropTypes.bool,
  centered: PropTypes.bool,
  scrollable: PropTypes.bool,
  className: PropTypes.string,
  overlayClassName: PropTypes.string,
  enableTailwind: PropTypes.bool,
};

/**
 * Modal Header Component
 */
export const ModalHeader = ({
  children,
  closeButton = true,
  className = ''
}) => {
  // NOTE: do NOT pass onHide here. react-bootstrap's close button already calls the
  // Modal's onHide via context (AbstractModalHeader: context.onHide() THEN onHide()),
  // so passing an explicit onHide made the X fire onClose TWICE — which double-counted
  // Upgrade Prompt Dismissed (GROW-73). The Modal-level onHide (see <BSModal onHide>) is
  // the single source of truth for closing.
  return (
    <BSModal.Header closeButton={closeButton} className={className}>
      {children}
    </BSModal.Header>
  );
};

/**
 * Modal Title Component
 */
export const ModalTitle = ({ children, className = '' }) => {
  return (
    <BSModal.Title className={className}>
      {children}
    </BSModal.Title>
  );
};

/**
 * Modal Body Component
 */
export const ModalBody = ({ children, className = '' }) => {
  return (
    <BSModal.Body className={className}>
      {children}
    </BSModal.Body>
  );
};

/**
 * Modal Footer Component
 */
export const ModalFooter = ({ children, className = '' }) => {
  return (
    <BSModal.Footer className={className}>
      {children}
    </BSModal.Footer>
  );
};

/**
 * Confirmation Modal - Pre-configured modal for confirmations
 */
export const ConfirmModal = ({
  isOpen,
  onClose,
  onConfirm,
  title = 'Confirm Action',
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'primary',
  ...props
}) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="sm" {...props}>
      <ModalHeader closeButton>
        <ModalTitle>{title}</ModalTitle>
      </ModalHeader>
      <ModalBody>
        <p className="text-muted">{message}</p>
      </ModalBody>
      <ModalFooter>
        <button
          type="button"
          onClick={onClose}
          className="btn btn-outline-secondary"
        >
          {cancelText}
        </button>
        <button
          type="button"
          onClick={() => {
            if (onConfirm) onConfirm();
            if (onClose) onClose();
          }}
          className={`btn ${variant === 'danger' ? 'btn-danger' : 'btn-primary'}`}
        >
          {confirmText}
        </button>
      </ModalFooter>
    </Modal>
  );
};

export default Modal;

